import {
  Purchases,
  PURCHASES_ERROR_CODE,
  PRODUCT_CATEGORY,
  PACKAGE_TYPE,
  INTRO_ELIGIBILITY_STATUS,
} from '@revenuecat/purchases-capacitor';
import type {
  PurchasesIntroPrice,
  PurchasesPackage,
  PurchasesStoreProduct,
} from '@revenuecat/purchases-capacitor';
import { nativePlatform, openExternal } from './platform';
import { getAppsFlyerUid, resolveAttConsentStatus } from './appsflyer';

/**
 * RevenueCat purchase layer (native only). Store purchases report to
 * RevenueCat, whose webhook drives the backend membership row + session
 * claims — the backend stays the single entitlement authority, so nothing
 * here reads entitlements from the RevenueCat SDK.
 */

let configuredForUserId: string | null = null;

/**
 * Configure the SDK once per launch and pin purchases to our better-auth user
 * id (the webhook resolves the row through this app_user_id). Safe to call
 * repeatedly; re-logs-in when a different user signs in.
 */
export const ensureRevenueCat = async (userId: string): Promise<void> => {
  const platform = nativePlatform();
  if (!platform) {
    throw new Error('RevenueCat is only available in the native app');
  }
  if (configuredForUserId === userId) return;

  if (configuredForUserId === null) {
    const apiKey =
      platform === 'ios'
        ? process.env.REACT_APP_RC_API_KEY_APPLE
        : process.env.REACT_APP_RC_API_KEY_GOOGLE;
    if (!apiKey) {
      throw new Error('RevenueCat API key not configured for this platform');
    }
    await Purchases.configure({ apiKey, appUserID: userId });
  } else {
    await Purchases.logIn({ appUserID: userId });
  }

  // Attribution keys for RevenueCat's server-side AppsFlyer integration, which
  // is what reports store revenue to the ad networks (the client never logs a
  // purchase event — see lib/appsflyer.ts). They must be set BEFORE the first
  // purchase, and every purchase path awaits this function first, so this is
  // the one place that ordering is structurally guaranteed. RevenueCat rejects
  // attribute calls made before configure(), which rules out doing this at app
  // launch. Best-effort throughout: attribution must never abort a sale.
  try {
    // Fallback match keys ($idfa/$idfv/$ip) — these let AppsFlyer resolve the
    // device even if the AppsFlyer id below is unavailable.
    await Purchases.collectDeviceIdentifiers();

    const appsflyerID = await getAppsFlyerUid();
    if (appsflyerID) {
      await Purchases.setAppsflyerID({ appsflyerID });
    }

    const attConsentStatus = await resolveAttConsentStatus();
    if (attConsentStatus) {
      await Purchases.setAttributes({ $attConsentStatus: attConsentStatus });
    }
  } catch {
    // Attribution is best-effort — a purchase must still go through
  }

  configuredForUserId = userId;
};

const isUserCancellation = (error: unknown): boolean => {
  const code = (error as { code?: string })?.code;
  return code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR;
};

/**
 * Purchase the store product with the given identifier through the native
 * payment sheet. Resolves 'cancelled' (not an error) when the user dismisses
 * the sheet. Entitlements land via the RevenueCat webhook seconds later — the
 * caller polls the session for the claim flip (the /membership-success flow).
 */
export const purchaseProduct = async (
  userId: string,
  productId: string
): Promise<'purchased' | 'cancelled'> => {
  await ensureRevenueCat(userId);

  const { current, all } = await Purchases.getOfferings();
  const packages = [
    ...(current?.availablePackages ?? []),
    ...Object.values(all ?? {}).flatMap((offering) => offering.availablePackages),
  ];
  const matched = packages.find((pkg) => pkg.product.identifier === productId);
  if (!matched) {
    throw new Error('This plan is not available right now — please try again later');
  }

  try {
    await Purchases.purchasePackage({ aPackage: matched });
    return 'purchased';
  } catch (error) {
    if (isUserCancellation(error)) return 'cancelled';
    throw error;
  }
};

/**
 * Purchase a CONSUMABLE store product (AI credit packs) through the native
 * payment sheet. Consumables aren't attached to subscription offerings, so
 * they're fetched directly by identifier rather than through packages. The
 * credits land via the RevenueCat webhook (NON_RENEWING_PURCHASE) seconds
 * later — the caller refetches the quota/balance rather than session claims.
 */
export const purchaseConsumable = async (
  userId: string,
  productId: string
): Promise<'purchased' | 'cancelled'> => {
  await ensureRevenueCat(userId);

  const { products } = await Purchases.getProducts({
    productIdentifiers: [productId],
    type: PRODUCT_CATEGORY.NON_SUBSCRIPTION,
  });
  const product = products[0];
  if (!product) {
    throw new Error('This credits pack is not available right now — please try again later');
  }

  try {
    await Purchases.purchaseStoreProduct({ product });
    return 'purchased';
  } catch (error) {
    if (isUserCancellation(error)) return 'cancelled';
    throw error;
  }
};

/**
 * Re-sync previous store purchases to this account (an Apple review
 * requirement for subscription apps — e.g. after a reinstall).
 */
export const restorePurchases = async (userId: string): Promise<void> => {
  await ensureRevenueCat(userId);
  await Purchases.restorePurchases();
};

export interface StoreProductPricing {
  price: number;
  priceString: string;
  currencyCode: string;
  /**
   * Introductory price, when the product carries one (the exit offer's
   * first-term discount). Subscriptions only — absent for consumables.
   */
  introPrice?: PurchasesIntroPrice | null;
}

/**
 * Store pricing for every configured product, keyed by product identifier.
 * App Store / Play prices differ from the web prices (store commission is
 * priced in), so the native paywall must display these, never the hardcoded
 * web figures — the payment sheet would contradict them.
 */
export const getStoreProductPricing = async (
  userId: string
): Promise<Record<string, StoreProductPricing>> => {
  await ensureRevenueCat(userId);
  const { current, all } = await Purchases.getOfferings();
  const packages = [
    ...(current?.availablePackages ?? []),
    ...Object.values(all ?? {}).flatMap((offering) => offering.availablePackages),
  ];
  const pricing: Record<string, StoreProductPricing> = {};
  for (const pkg of packages) {
    pricing[pkg.product.identifier] = {
      price: pkg.product.price,
      priceString: pkg.product.priceString,
      currencyCode: pkg.product.currencyCode,
      introPrice: pkg.product.introPrice ?? null,
    };
  }
  return pricing;
};

/**
 * The package a specific RevenueCat offering sells (its first package), or
 * null when the offering doesn't exist or has no fetchable packages. This is
 * what lets the dashboard re-target a surface (e.g. the exit sheet's
 * `exit_offer` offering) to a different product with no app release. The
 * caller checks `isAnnual` because the sheet's wording ("your first year") is
 * baked into the binary. Cheap to call: the SDK caches offerings for the app
 * session.
 */
export const getOfferingPackage = async (
  userId: string,
  offeringIdentifier: string
): Promise<{ productId: string; isAnnual: boolean } | null> => {
  await ensureRevenueCat(userId);
  const { all } = await Purchases.getOfferings();
  const pkg = all?.[offeringIdentifier]?.availablePackages[0];
  if (!pkg) return null;
  return {
    productId: pkg.product.identifier,
    isAnnual: pkg.packageType === PACKAGE_TYPE.ANNUAL,
  };
};

/** The two standard packages the paywall sells from the CURRENT offering. */
const PAYWALL_PACKAGE_IDS = { monthly: '$rc_monthly', annual: '$rc_annual' } as const;

export interface PaywallPackage {
  /** RevenueCat package identifier: "$rc_monthly" or "$rc_annual". */
  identifier: string;
  productId: string;
  price: number;
  priceString: string;
  currencyCode: string;
  /**
   * Length of the product's free trial in days, when it carries one AND this
   * user can still redeem it (Apple grants one intro per Apple ID per
   * subscription group). Null means the CTA must not promise a trial.
   */
  freeTrialDays: number | null;
}

export interface PaywallPackages {
  monthly: PaywallPackage | null;
  annual: PaywallPackage | null;
}

const PERIOD_UNIT_DAYS: Record<string, number> = { DAY: 1, WEEK: 7, MONTH: 30, YEAR: 365 };

/**
 * Days of free trial the store reports for a product. A trial is an intro
 * offer whose price is zero (a pay-up-front intro like the exit offer is NOT a
 * trial). Play surfaces the free phase on the subscription option rather than
 * `introPrice`, so both are checked.
 */
const freeTrialDaysOf = (product: PurchasesStoreProduct): number | null => {
  const intro = product.introPrice;
  if (intro && intro.price === 0) {
    const unitDays = PERIOD_UNIT_DAYS[intro.periodUnit];
    return unitDays ? unitDays * intro.periodNumberOfUnits * intro.cycles : null;
  }
  const freePhase = product.defaultOption?.freePhase;
  if (freePhase) {
    const unitDays = PERIOD_UNIT_DAYS[freePhase.billingPeriod.unit];
    return unitDays
      ? unitDays * freePhase.billingPeriod.value * (freePhase.billingCycleCount ?? 1)
      : null;
  }
  return null;
};

/**
 * The monthly and annual packages of the current offering, with everything
 * the paywall displays read from the store (prices, per-month equivalent,
 * trial) and the trial already filtered by this user's eligibility. Either
 * side is null when the offering doesn't carry that package.
 */
export const getPaywallPackages = async (userId: string): Promise<PaywallPackages> => {
  await ensureRevenueCat(userId);
  const { current } = await Purchases.getOfferings();
  const findPackage = (identifier: string): PurchasesPackage | null =>
    current?.availablePackages.find((pkg) => pkg.identifier === identifier) ?? null;
  const monthly = findPackage(PAYWALL_PACKAGE_IDS.monthly);
  const annual = findPackage(PAYWALL_PACKAGE_IDS.annual);

  const productIdentifiers = [monthly, annual]
    .filter((pkg): pkg is PurchasesPackage => pkg !== null)
    .map((pkg) => pkg.product.identifier);
  // Errors stay permissive, matching isIntroOfferEligible: the store served
  // the trial, and the payment sheet always states the real charge.
  const eligibility: Record<string, { status: INTRO_ELIGIBILITY_STATUS } | undefined> =
    productIdentifiers.length > 0
      ? await Purchases.checkTrialOrIntroductoryPriceEligibility({
          productIdentifiers,
        }).catch(() => ({}))
      : {};

  const toPaywallPackage = (pkg: PurchasesPackage | null): PaywallPackage | null => {
    if (!pkg) return null;
    const ineligible =
      eligibility[pkg.product.identifier]?.status ===
      INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_INELIGIBLE;
    return {
      identifier: pkg.identifier,
      productId: pkg.product.identifier,
      price: pkg.product.price,
      priceString: pkg.product.priceString,
      currencyCode: pkg.product.currencyCode,
      freeTrialDays: ineligible ? null : freeTrialDaysOf(pkg.product),
    };
  };
  return { monthly: toPaywallPackage(monthly), annual: toPaywallPackage(annual) };
};

/**
 * Whether this user can actually be charged a product's introductory price.
 * Apple grants one intro per Apple ID per subscription group, so a returning
 * subscriber who already redeemed one would be charged the FULL price no
 * matter what the UI displays — surfaces offering an intro figure must hide
 * themselves when this returns false. Only a definitive INELIGIBLE blocks:
 * UNKNOWN stays permissive (Android always reports it), and
 * NO_INTRO_OFFER_EXISTS is deliberately ignored — sandbox misreports it for
 * freshly configured intros (RC community-documented), and every caller
 * already requires a store-served introPrice on the product before showing
 * anything, which is the authoritative "an intro exists" signal.
 */
export const isIntroOfferEligible = async (
  userId: string,
  productId: string
): Promise<boolean> => {
  await ensureRevenueCat(userId);
  const result = await Purchases.checkTrialOrIntroductoryPriceEligibility({
    productIdentifiers: [productId],
  });
  const status = result[productId]?.status;
  return status !== INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_INELIGIBLE;
};

/**
 * Store pricing for CONSUMABLE products (AI credit packs), keyed by product
 * identifier. Consumables aren't attached to subscription offerings, so
 * getStoreProductPricing never sees them — they must be fetched directly by
 * identifier, exactly like purchaseConsumable buys them.
 */
export const getConsumablePricing = async (
  userId: string,
  productIds: string[]
): Promise<Record<string, StoreProductPricing>> => {
  await ensureRevenueCat(userId);
  const { products } = await Purchases.getProducts({
    productIdentifiers: productIds,
    type: PRODUCT_CATEGORY.NON_SUBSCRIPTION,
  });
  const pricing: Record<string, StoreProductPricing> = {};
  for (const product of products) {
    pricing[product.identifier] = {
      price: product.price,
      priceString: product.priceString,
      currencyCode: product.currencyCode,
    };
  }
  return pricing;
};

const STORE_SUBSCRIPTION_URLS = {
  ios: 'https://apps.apple.com/account/subscriptions',
  android: 'https://play.google.com/store/account/subscriptions',
} as const;

/**
 * Open the OS subscription-management surface (iOS subscription settings /
 * Play Store subscriptions). Upgrades, downgrades, and cancellation of
 * store-billed subscriptions all happen there. RevenueCat's managementURL
 * deep-links to the exact subscription when available; the generic store
 * subscriptions page is the fallback.
 */
export const showManageSubscriptions = async (userId: string): Promise<void> => {
  await ensureRevenueCat(userId);
  const { customerInfo } = await Purchases.getCustomerInfo();
  const platform = nativePlatform() ?? 'ios';
  await openExternal(customerInfo.managementURL ?? STORE_SUBSCRIPTION_URLS[platform]);
};
